import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LibraryBooks } from "@mui/icons-material";
import {
  attachToPrompt as attachEmbeddedToPrompt,
  deleteDocument,
  importFromStoragePath,
  uploadDocument as uploadEmbeddedDocument,
} from "services/embeddedDocumentsService";
import { uploadDocument as uploadClinicalReference } from "services/clinicalReferencesService";
import promptRegistryService from "services/llm/promptRegistryService";
import { db } from "services/firebase";
import { useAuth } from "hooks/useAuth";
import DocumentUploadZone from "./DocumentUploadZone";
import DocumentTable from "./DocumentTable";
import DocumentLibraryDialog from "./DocumentLibraryDialog";
import BulkDeleteConfirmDialog from "./BulkDeleteConfirmDialog";
import {
  compareByFileName,
  docMatchesPrompt,
  hidePendingDuplicates,
} from "./documentListUtils";

const DEFAULT_ACCEPTED_TYPES = [
  ".pdf", ".doc", ".docx", ".odf", ".odt", ".ppt", ".pptx", ".md", ".txt",
];
const STORAGE_QUOTA_BYTES = 10 * 1024 * 1024 * 1024; // 10 GiB

const DocumentManagementPanel = ({
  disabled = false,
  promptId = "",
  organizationId = "",
  chunkLimit = 5,
  onChunkLimitChange,
  scope = "global",
  initialPrioritizeUploadedDocs = false,
}) => {
  const normalizedPromptId = String(promptId || "").trim().toLowerCase();
  const hasPromptId = Boolean(normalizedPromptId);

  const {
    embeddedDocs: cachedEmbeddedDocs,
    clinicalRefs: cachedClinicalRefs,
    chartmindDocsLoading,
    userData,
  } = useAuth();
  const documents = useMemo(
    () =>
      (cachedEmbeddedDocs || [])
        .filter((doc) => docMatchesPrompt(doc, normalizedPromptId))
        .sort(compareByFileName),
    [cachedEmbeddedDocs, normalizedPromptId],
  );
  const documentsLoading = chartmindDocsLoading;

  const [prioritizeUploadedDocs, setPrioritizeUploadedDocs] = useState(
    Boolean(initialPrioritizeUploadedDocs),
  );
  const [savingPrioritize, setSavingPrioritize] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [storageLimitReached, setStorageLimitReached] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [deleteDialogDocs, setDeleteDialogDocs] = useState([]);

  // Hide a pending placeholder as soon as its uploaded row arrives via the live
  // snapshot, so a document never appears twice mid-batch.
  const visibleDocuments = useMemo(
    () => [...hidePendingDuplicates(pendingFiles, documents), ...documents],
    [pendingFiles, documents],
  );

  // Re-sync the switch when the dialog opens for a different prompt.
  useEffect(() => {
    setPrioritizeUploadedDocs(Boolean(initialPrioritizeUploadedDocs));
  }, [initialPrioritizeUploadedDocs, normalizedPromptId]);

  // Persist immediately on toggle (like document uploads, no Save needed).
  // Global editors write llmGlobalPrompts; org admins write their llmLocalPrompts
  // org layer, which overrides the global value at runtime in callLLMAPI.
  const persistPrioritizeSetting = async (nextValue) => {
    setPrioritizeUploadedDocs(nextValue);
    setSavingPrioritize(true);
    setUploadError(null);
    try {
      await promptRegistryService.updatePrompt({
        id: normalizedPromptId,
        updates: { prioritizeUploadedDocs: nextValue },
        reason: nextValue
          ? "Enabled prioritize uploaded documents"
          : "Disabled prioritize uploaded documents",
        actorUid: userData?.uid || null,
        actorEmail: userData?.email || null,
        organizationId: scope === "org" ? organizationId || null : null,
        isGlobalAdmin: scope === "global",
      });
      setSuccessMessage(
        nextValue
          ? "Uploaded documents will be prioritized in model responses."
          : "Uploaded documents will no longer be prioritized in model responses.",
      );
    } catch (err) {
      console.error("[DocumentManagementPanel] failed to save prioritize setting:", err);
      setPrioritizeUploadedDocs(!nextValue); // revert
      setUploadError("Failed to save the setting: " + (err.message || "Unknown error"));
    } finally {
      setSavingPrioritize(false);
    }
  };

  const normalizePendingFile = (file) => ({
    id: `pending-${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    fileName: file.name,
    metadata: {
      fileType: file.name.split(".").pop() || "file",
    },
    createdAt: new Date(),
    isPending: true,
  });

  const uploadEmbedded = async (file) => {
    const pendingId = `pending-${file.name}-${file.size}-${file.lastModified}`;
    await uploadEmbeddedDocument(
      file,
      normalizedPromptId,
      {},
      {
        onReadProgress: (ratio) => {
          setPendingFiles((current) =>
            current.map((f) =>
              f.id === pendingId
                ? { ...f, uploadStage: "reading", readProgress: Math.round(ratio * 100) }
                : f,
            ),
          );
        },
        onUploading: () => {
          setPendingFiles((current) =>
            current.map((f) =>
              f.id === pendingId
                ? { ...f, uploadStage: "uploading", readProgress: 100 }
                : f,
            ),
          );
        },
      },
      scope,
    );
  };

  const acceptFiles = async (files, includeClinical) => {
    if (!files.length || !hasPromptId) return;

    if (organizationId) {
      try {
        const orgSnap = await db.collection("organizations").doc(organizationId).get();
        const usedBytes = Number(orgSnap.data()?.storageUsedBytes) || 0;
        if (usedBytes >= STORAGE_QUOTA_BYTES) {
          setStorageLimitReached(true);
          return;
        }
      } catch (err) {
        console.error("[DocumentManagementPanel] storage limit check failed:", err);
      }
    }

    setUploadError(null);
    setSuccessMessage(null);
    const normalized = files.map((file) => ({
      ...normalizePendingFile(file),
      uploadStage: "reading",
      readProgress: 0,
    }));

    setPendingFiles((current) => {
      const existing = new Set(current.map((f) => f.id));
      const merged = [...current];
      normalized.forEach((f) => {
        if (!existing.has(f.id)) merged.push(f);
      });
      return merged;
    });

    const errors = [];
    const BATCH = 5;
    let done = 0;

    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });

    for (let i = 0; i < files.length; i += BATCH) {
      await Promise.all(
        files.slice(i, i + BATCH).map(async (file, j) => {
          const fileIndex = i + j;
          const pendingId = normalized[fileIndex].id;
          try {
            const tasks = [uploadEmbedded(file)];
            if (includeClinical) {
              tasks.push(uploadClinicalReference(file, normalizedPromptId, {}, scope));
            }
            await Promise.all(tasks);
          } catch (err) {
            console.error("[DocumentManagementPanel] upload failed:", file.name, err);
            errors.push(file.name);
            setPendingFiles((current) => current.filter((f) => f.id !== pendingId));
          } finally {
            done += 1;
            setUploadProgress({ done, total: files.length });
          }
        }),
      );
    }

    setUploading(false);

    if (errors.length) {
      setUploadError(`Failed to upload: ${errors.join(", ")}`);
    }

    const succeeded = files.length - errors.length;
    if (succeeded > 0) {
      const noun = succeeded === 1 ? "document" : "documents";
      const where = includeClinical ? " (also added to Clinical References)" : "";
      setSuccessMessage(`Uploaded ${succeeded} ${noun}${where}.`);
    }

    setPendingFiles((current) =>
      current.filter((f) => errors.some((name) => f.fileName === name)),
    );
  };

  const handleRemove = async (doc) => {
    if (doc.isPending) {
      setPendingFiles((current) => current.filter((entry) => entry.id !== doc.id));
      return;
    }
    // Route single-row remove through the same confirm dialog so behavior
    // is consistent with bulk removal.
    setDeleteDialogDocs([doc]);
  };

  const toggleSelected = (docId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectedDocs = useMemo(
    () => documents.filter((doc) => selectedIds.has(doc.id)),
    [documents, selectedIds],
  );

  const handleBulkDeleteClick = () => {
    if (!selectedDocs.length) return;
    setDeleteDialogDocs(selectedDocs);
  };

  // Removal only detaches from this prompt; files stay in the library.
  // Permanent deletion lives on the Disk Usage page.
  const handleConfirmDelete = async () => {
    if (!deleteDialogDocs.length) return;
    setSuccessMessage(null);
    setUploadError(null);
    const ids = deleteDialogDocs.map((doc) => doc.id);
    await deleteDocument(ids, normalizedPromptId, { detachOnly: true });
    setSelectedIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setSuccessMessage(
      `Removed ${ids.length} ${ids.length === 1 ? "document" : "documents"} from this prompt.`,
    );
    setDeleteDialogDocs([]);
  };

  const handleLibraryAttached = ({ attachedCount, errors }) => {
    if (attachedCount > 0) {
      setSuccessMessage(
        `Attached ${attachedCount} ${attachedCount === 1 ? "document" : "documents"} from your library.`,
      );
    }
    if (errors?.length) {
      setUploadError(
        `Some attachments failed: ${errors.map((e) => e.error || e.docId).join(", ")}`,
      );
    }
  };

  // Keeps track of which library docs came from which collection so attachCombinedToPrompt
  // can route them correctly (embedded → update promptIds; clinical → download + re-embed).
  const libraryDocInfoRef = useRef(new Map());

  // Combined library list, derived from the Auth cache. Clinical-ref docs have their
  // promptIds cleared in this view so they don't show as "Already attached" in the
  // embedded-docs picker (they live in a different collection with different IDs).
  // We dedupe by contentHash: when a clinical ref has been "imported" (i.e. an embedded
  // doc with the same contentHash already exists), we hide the clinical original so
  // only the attachable embedded version shows.
  const combinedLibraryDocs = useMemo(() => {
    const embeds = (cachedEmbeddedDocs || []).map((d) => ({
      ...d,
      _source: "embedded",
    }));
    const embeddedHashes = new Set(
      embeds.map((d) => d.contentHash).filter(Boolean),
    );
    const clinicals = (cachedClinicalRefs || [])
      .filter((d) => !d.contentHash || !embeddedHashes.has(d.contentHash))
      .map((d) => ({
        ...d,
        _source: "clinical",
        promptIds: [],
      }));
    const docs = [...embeds, ...clinicals];
    libraryDocInfoRef.current = new Map(docs.map((d) => [d.id, d]));
    return docs;
  }, [cachedEmbeddedDocs, cachedClinicalRefs]);

  const attachCombinedToPrompt = useMemo(
    () =>
      async (docIds, normalizedPromptId, onProgress) => {
        const embeddedIds = [];
        const clinicalDocs = [];
        for (const id of docIds) {
          const doc = libraryDocInfoRef.current.get(id);
          if (doc?._source === "clinical") clinicalDocs.push(doc);
          else embeddedIds.push(id);
        }

        const attached = [];
        const errors = [];

        if (embeddedIds.length > 0) {
          try {
            const result = await attachEmbeddedToPrompt(embeddedIds, normalizedPromptId);
            attached.push(...(Array.isArray(result?.attached) ? result.attached : []));
            errors.push(...(Array.isArray(result?.errors) ? result.errors : []));
          } catch (err) {
            embeddedIds.forEach((id) => errors.push({ docId: id, error: err.message }));
          }
        }

        // Clinical reference docs have no embeddings/chunks yet, so they must be ingested
        // once as embedded documents. Their file already lives in Storage, so we pass its
        // storagePath and let the backend read it server-side (no browser download/re-upload);
        // the backend dedupes by contentHash, so re-importing the same content just attaches.
        // fetch(downloadURL) remains as a fallback for legacy docs without a storagePath.
        // When ingests cold-start, the platform can bounce a request before it
        // reaches our code — surfacing as a failed CORS preflight / network error
        // (no HTTP status), or a 5xx/429, or codes like "internal"/"unavailable".
        // Treat all of those as transient and retry. We do NOT retry deterministic
        // errors (e.g. invalid-argument for a bad file) — those would just fail
        // again. A bare network failure has no useful code, so an empty/unknown
        // code is treated as transient too.
        const isTransient = (err) => {
          const code = String(err?.code || "").toLowerCase();
          const message = String(err?.message || "").toLowerCase();
          if (/invalid-argument|not-found|permission|unauthenticated|already-exists/.test(code)) {
            return false;
          }
          if (!code || /internal|unavailable|deadline|cancelled|unknown|aborted|resource-exhausted/.test(code)) {
            return true;
          }
          return /\b(500|502|503|504|429)\b|fetch|network|cors|access control|preflight/.test(message);
        };
        const runIngest = async (doc) => {
          if (doc.storagePath) {
            return importFromStoragePath(doc, normalizedPromptId, doc.metadata || {}, scope);
          }
          if (!doc.downloadURL) throw new Error("No download URL available.");
          const resp = await fetch(doc.downloadURL);
          if (!resp.ok) throw new Error(`Download failed (${resp.status}).`);
          const blob = await resp.blob();
          const file = new File(
            [blob],
            doc.fileName || "document",
            { type: blob.type || "application/octet-stream" },
          );
          return uploadEmbeddedDocument(file, normalizedPromptId, doc.metadata || {}, {}, scope);
        };
        // Escalating backoff gives the platform time to finish a cold start before
        // we retry, so a retry doesn't just collide with the same scale-up.
        const RETRY_BACKOFF_MS = [2000, 4000, 8000];
        const importOne = async (doc) => {
          const startedAt = Date.now();
          const via = doc.storagePath ? "storagePath" : "download+reupload";
          let lastErr = null;
          const maxAttempts = RETRY_BACKOFF_MS.length + 1;
          for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            try {
              const result = await runIngest(doc);
              const ingested = result?.files?.[0];
              console.log("[DocumentManagementPanel] clinical→embedded import", {
                fileName: doc.fileName,
                via,
                attempt,
                ms: Date.now() - startedAt,
                // reused=true means the backend matched an already-embedded doc by
                // contentHash and just attached it (no re-embedding happened).
                reused: ingested?.reused === true,
                chunkCount: ingested?.chunkCount ?? null,
              });
              attached.push({ docId: doc.id });
              return;
            } catch (err) {
              lastErr = err;
              if (attempt < maxAttempts && isTransient(err)) {
                console.warn("[DocumentManagementPanel] transient import error, retrying:", doc.id, `attempt ${attempt}`, err?.code || err?.message);
                await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt - 1]));
                continue;
              }
              break;
            }
          }
          console.error("[DocumentManagementPanel] clinical→embedded import failed:", doc.id, via, lastErr);
          errors.push({ docId: doc.id, error: lastErr?.message || "Import failed." });
        };
        // Import one document at a time. Each import is a full server-side ingest
        // on its own function instance; firing several at once forces simultaneous
        // cold starts, and the platform bounces the overflow (failed preflight /
        // 503) before our code can even run. Serializing keeps the first instance
        // warm for the rest to reuse, which eliminates the cold-start storm. The
        // retry above covers any remaining blip.
        const IMPORT_BATCH = 1;
        let importedCount = 0;
        const total = clinicalDocs.length;
        if (total > 0) onProgress?.({ done: 0, total });
        for (let i = 0; i < clinicalDocs.length; i += IMPORT_BATCH) {
          await Promise.all(
            clinicalDocs.slice(i, i + IMPORT_BATCH).map(async (doc) => {
              await importOne(doc);
              importedCount += 1;
              onProgress?.({ done: importedCount, total });
            }),
          );
        }

        return { attached, errors };
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope],
  );

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={prioritizeUploadedDocs}
            onChange={(event) => persistPrioritizeSetting(event.target.checked)}
            disabled={
              disabled ||
              savingPrioritize ||
              !hasPromptId ||
              (scope === "org" && !organizationId)
            }
          />
        }
        label="Prioritize uploaded documents in model responses"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
        Turn this on if you want the assistant to rely on these uploaded
        documents as the main source of truth when answering. If you leave it
        off, the assistant can still use general medical knowledge and may treat
        uploaded files as secondary context. Saves automatically when toggled.
        {scope === "org"
          ? " This setting applies to your organization."
          : ""}
      </Typography>
      {!hasPromptId ? (
        <Typography variant="body2" color="text.secondary">
          Enter a prompt ID before uploading embedded documents.
        </Typography>
      ) : null}
      <DocumentUploadZone
        disabled={disabled}
        hasPromptId={hasPromptId}
        acceptedExtensions={DEFAULT_ACCEPTED_TYPES}
        inputId="embedded-documents-upload"
        secondaryTarget={{
          value: "clinical",
          label: "Also add to Clinical References",
        }}
        onFilesAccepted={acceptFiles}
        trailing={
          hasPromptId ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<LibraryBooks />}
              onClick={() => setLibraryOpen(true)}
              disabled={disabled || uploading}
            >
              Add from library
            </Button>
          ) : null
        }
      />

      <TextField
        type="number"
        label="Included Chunks"
        value={chunkLimit}
        onChange={(event) => {
          const nextValue = Number(event.target.value);
          if (!Number.isFinite(nextValue)) return;
          const clampedValue = Math.min(30, Math.max(1, Math.floor(nextValue)));
          onChunkLimitChange?.(clampedValue);
        }}
        inputProps={{ min: 1, max: 30, step: 1 }}
        disabled={disabled || !hasPromptId}
        helperText="When you run Try Prompt, this many top-matching excerpts from the documents above are pulled in as context for the LLM (1–30)."
        sx={{ maxWidth: 480 }}
      />

      <Divider />

      {uploading ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            Uploading {uploadProgress.done} / {uploadProgress.total}…
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress.total > 0 ? (uploadProgress.done / uploadProgress.total) * 100 : 0}
          />
        </Box>
      ) : null}

      {uploadError ? (
        <Alert severity="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      ) : null}
      {successMessage ? (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      ) : null}

      {/* minHeight reserves room for the selection buttons so the header does not shift */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", minHeight: 28 }}>
        <Typography variant="subtitle2">
          Documents{documents.length ? ` (${documents.length})` : ""}
        </Typography>
        {selectedIds.size > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {selectedIds.size} selected
            </Typography>
            <Button
              size="small"
              sx={{ minWidth: 0, px: 1, py: 0.25 }}
              onClick={clearSelection}
              disabled={disabled}
            >
              Clear
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              sx={{ px: 1, py: 0.25 }}
              onClick={handleBulkDeleteClick}
              disabled={disabled}
            >
              Remove selected
            </Button>
          </Stack>
        ) : null}
      </Box>
      <DocumentTable
        documents={visibleDocuments}
        loading={documentsLoading}
        emptyMessage="No documents uploaded yet."
        disabled={disabled}
        selection={{ selectedIds, onToggle: toggleSelected }}
        isRowDisabled={(doc) => Boolean(doc.isPending)}
        onRemove={handleRemove}
        getExtraChips={(doc) => {
          const fileType = doc.metadata?.fileType || doc.type;
          return fileType
            ? [{ label: String(fileType).toUpperCase(), variant: "outlined" }]
            : [];
        }}
        getEndAdornment={(doc) => {
          if (doc.isPending) return null;
          const count = Number(doc.chunkCount || 0);
          return `${count} ${count === 1 ? "chunk" : "chunks"}`;
        }}
      />

      <DocumentLibraryDialog
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        promptId={normalizedPromptId}
        title="Add documents from your library"
        libraryDocuments={combinedLibraryDocs}
        loading={chartmindDocsLoading}
        attachToPrompt={attachCombinedToPrompt}
        onAttached={handleLibraryAttached}
        getExtraChips={(doc) =>
          doc._source === "clinical"
            ? [{ label: "Clinical reference", color: "info" }]
            : []
        }
      />

      <BulkDeleteConfirmDialog
        open={deleteDialogDocs.length > 0}
        documents={deleteDialogDocs}
        onClose={() => setDeleteDialogDocs([])}
        onConfirm={handleConfirmDelete}
        resourceNoun="document"
      />

      <Dialog open={storageLimitReached} onClose={() => setStorageLimitReached(false)}>
        <DialogTitle>Storage limit reached</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your organization has reached the 10 GB storage limit. Remove existing
            documents before uploading new ones.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStorageLimitReached(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentManagementPanel;
